import React from 'react';
import { AlertCircle, ChevronDown } from 'lucide-react';

/**
 * ModernSelect - Clean, Minimal Select Component
 * Consistent styling across the template builder
 */
const ModernSelect = ({
  label,
  value,
  onChange,
  options,
  placeholder,
  required = false,
  className = "",
  validationRules = {},
  fieldPath = "",
  showValidation = true,
  description = "",
  validationErrors = {},
  setValidationErrors = () => {},
  disabled = false
}) => {
  const error = validationErrors[fieldPath];
  const hasError = error && showValidation;

  const validateField = (path, value, rules = {}) => {
    const errors = [];
    if (rules.required && (!value || value.toString().trim() === '')) {
      errors.push('This field is required');
    }
    return errors.length > 0 ? errors[0] : null;
  };

  return (
    <div className={`space-y-1.5 ${className}`}>
      {label && (
        <label className="block text-sm font-medium text-gray-700">
          {label}
          {required && <span className="text-red-500 ml-0.5">*</span>}
        </label>
      )}
      {description && (
        <p className="text-xs text-gray-500">{description}</p>
      )}
      <div className="relative">
        <select
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          onBlur={(e) => {
            if (fieldPath && validationRules && Object.keys(validationRules).length > 0) {
              const error = validateField(fieldPath, e.target.value, validationRules);
              if (error) {
                setValidationErrors(prev => ({ ...prev, [fieldPath]: error }));
              } else {
                setValidationErrors(prev => {
                  const newErrors = { ...prev };
                  delete newErrors[fieldPath];
                  return newErrors;
                });
              }
            }
          }}
          disabled={disabled}
          className={`
            w-full px-3 py-2.5 pr-10 text-sm
            border rounded-lg
            appearance-none
            transition-all duration-150
            disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed
            ${!value ? 'text-gray-400' : 'text-gray-900'}
            ${hasError
              ? 'border-red-300 bg-red-50 focus:border-red-500 focus:ring-2 focus:ring-red-100'
              : 'border-gray-300 bg-white hover:border-gray-400 focus:border-primary focus:ring-2 focus:ring-primary/10'
            }
            focus:outline-none
          `}
        >
          {placeholder && (
            <option value="" disabled>
              {placeholder}
            </option>
          )}
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
          {hasError ? (
            <AlertCircle className="h-4 w-4 text-red-400" />
          ) : (
            <ChevronDown className="h-4 w-4 text-gray-400" />
          )}
        </div>
      </div>
      {hasError && (
        <p className="text-xs text-red-600 flex items-center gap-1">
          <AlertCircle className="w-3 h-3" />
          {error}
        </p>
      )}
    </div>
  );
};

export default ModernSelect;
